import { createFileRoute, useNavigate } from "@tanstack/react-router";
import "./index.css";
import { useEffect, useMemo, useRef, useState } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { appColors } from "@/constants/color";
import {
  FileUploader,
  FileUploaderRef,
} from "@/components/form/file-upload/fileUploader";

import {
  Path,
  UseFormRegister,
  FieldValues,
  FieldError,
  useForm, Controller
} from "react-hook-form";
// import {
//   capitalizeFirstLetterOfWords,
//   generateVariationSlug,
// } from "@/utils/common";
import { weightUnitOptions } from "@/features/seller";
import { get } from "lodash";
import { Product } from "@/interfaces";
import {
  finalProductCreationMapper,
  removeEmptyValues,
} from "@/features/business/product/mapper";
import { FormInput } from "@/components/common/formInput/formInput";
import { Button } from "@/components/ui/button";
import { useSelector } from "react-redux";
import { LIMIT } from "@/constants";
import { mapProductToEditableForm } from "@/features/product/mapper";
import { toast } from "react-toastify";
import {handlePrintBarcode,initialProductValue, useCreateProductMutation, useGetCreateProductListQuery, useGetProductUnitListQuery, useLazyGetStoreProductCountQuery } from "@/features/product";
import { FileTypeEnum } from "@/enum";
import { currentUserSelector } from "@/features/auth";
import { useGetBrandListQuery } from "@/features/brand";
import { useGetCategoryListQuery } from "@/features/category";
import { currencyFormatter } from "@/utils";

const numberValidation = z.preprocess(
  (val) => {
    if (typeof val === "string") {
      const trimmed = val.trim();
      if (trimmed === "") return undefined;
      const parsed = Number(trimmed);
      return isNaN(parsed) ? val : parsed;
    }
    return val;
  },
  z
    .number()
    .optional()
    .refine((val) => val === undefined || Number.isFinite(val), {
      message: "value must be a valid number",
    })
);
const VariationInfoSchema = z.object({
  size: z.object({ name: z.string().optional() }).optional(),
  weight: z
    .object({
      quantity: numberValidation.optional(),
      unit: z.string().optional(),
    })
    .optional(),
  color: z.object({ name: z.string().optional() }).optional(),
  flavor: z.object({ name: z.string().optional() }).optional(),
  imageList: z.array(z.string()).optional(),
  itemPackingQuantity: numberValidation.default(1).optional(),
});

const PriceSchema = z.object({
  mrp: numberValidation.optional(),
  purchasePrice: numberValidation.optional(),
  sellingPrice: numberValidation.optional(),
});

const StockSchema = z.object({
  productUnit: z.string().optional(),
  quantity: numberValidation.optional(),
  stockMinimumQuantity: numberValidation.optional(),
});

const MinimumQuantitySchema = z.object({
  quantity: numberValidation.optional().default(1),
  isMultiple: z.object({ enable: z.boolean().default(false) }).optional(),
});

const VariationSchema = z.object({
  price: PriceSchema.optional(),
  barcode: z
    .string()
    .optional()
    .refine(val => !val || val.length >= 3, {
      message: 'Barcode must be 3 or more characters if provided',
    }),
  productCode: z.string().optional(),
  variationInfo: VariationInfoSchema.optional(),
  stock: StockSchema.optional(),
  minimumQuantity: MinimumQuantitySchema.optional(),
});

const ProductSchema = z.object({
  _id: z.string().optional(),
  productCode: z.string().optional(),
  name: z.string(),
  description: z.string().optional(),
  brandId: z.string().optional(),
  barcode: z.string().optional(),
  categoryId: z.string().optional(),
  hsnCode: numberValidation.optional(),
  variationList: z.array(VariationSchema).optional(),
  taxPercentage: numberValidation.optional(),
  imageList: z.array(z.string()).optional(),
  subCategoryId: z.string().optional(),
  stock: StockSchema.optional(),
  mrp: numberValidation.refine(val => val !== undefined, {
    message: "MRP is required",
  }),
  purchasePrice: numberValidation.optional(),
  sellingPrice: numberValidation.optional(),
  size: z.object({ name: z.string().optional() }).optional(),
  weight: z
    .object({
      quantity: numberValidation.optional(),
      unit: z.string().optional(),
    })
    .optional(),
  color: z.object({ name: z.string().optional() }).optional(),
  flavor: z.object({ name: z.string().optional() }).optional(),
  minimumQuantity: MinimumQuantitySchema.optional(),
});

export type ProductForm = z.infer<typeof ProductSchema>;

// type Variation = z.infer<typeof VariationSchema>;
interface AddProductProps {
  onSuccess?: (response: any) => void;
  onClose?: () => void;
}

export const Route = createFileRoute("/(app)/(store)/product/add-product/")({
  component: RouteComponent,
});

export function RouteComponent({ onSuccess, onClose }: AddProductProps) {
  const {
    register,
    control,
    handleSubmit,
    watch,
    reset,
    setValue,
    setError,
    formState: { errors, dirtyFields },
  } = useForm<ProductForm>({
    resolver: zodResolver(ProductSchema),
    defaultValues: initialProductValue,
    'mode': 'onChange'
  });

  const mrpFormValue = watch('mrp');

  useEffect(() => {
    if (dirtyFields.mrp && !dirtyFields.sellingPrice) {
      if (mrpFormValue !== undefined && mrpFormValue !== null && String(mrpFormValue) !== '') {
        // Ensure no decimal is added if it's a whole number
        const formattedValue = Number.isInteger(Number(mrpFormValue)) 
          ? Number(mrpFormValue) 
          : mrpFormValue;
        setValue('sellingPrice', formattedValue as any, { shouldValidate: true });
      } else {
        setValue('sellingPrice', undefined as any, { shouldValidate: true });
      }
    }
  }, [mrpFormValue, setValue, dirtyFields.sellingPrice, dirtyFields.mrp]);

  // const { fields, append, remove, update } = useFieldArray({
  //   control,
  //   name: "variationList",
  // });
  const currentUser = useSelector(currentUserSelector);
  const [query, setQuery] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const dropdownRef = useRef<HTMLElement>(null)
  const [page] = useState(1);
  const { data: productList = [] } = useGetCreateProductListQuery({
    ['create-seller-product-detail']: true,
    ['seller-store']: currentUser?._id,
    name: query,
    limit: LIMIT * 2,
    page,
  });
  const navigate = useNavigate()
  const [createProduct , {isLoading}] = useCreateProductMutation();

  // const [isVariantModalOpen, setVariantModalOpen] = useState(false);
  // const [editingVariantIndex, setEditingVariantIndex] = useState<number | null>(
  //   null
  // );
  const { data: brandOptions = [] } = useGetBrandListQuery({
    limit: 10,
  });

  const { data: categoryOptions = [] } = useGetCategoryListQuery({
    limit: 10,
    name: "Product",
    type: "Product",
  });
  // const [currentVariant, setCurrentVariant] = useState<Variation | null>(null);

  // const handleAddVariant = (variant: Variation) => {
  //   if (editingVariantIndex !== null) {
  //     update(editingVariantIndex, variant);
  //   } else {
  //     append(variant);
  //   }
  //   setVariantModalOpen(false);
  //   setEditingVariantIndex(null);
  //   setCurrentVariant(null);
  // };

  // // Function to edit an existing variant
  // const handleEditVariant = (index: number) => {
  //   const variantToEdit = fields[index];
  //   setCurrentVariant(variantToEdit);
  //   setEditingVariantIndex(index);
  //   setVariantModalOpen(true);
  // };

  // // Function to remove a variant
  // const handleRemoveVariant = (index: number) => {
  //   remove(index);
  // };
  const imageList = watch('imageList') || [];
  const categoryId = watch('categoryId');
  const isBarcodeAvailable = watch('barcode');
  const weightUnit = watch('weight.unit');
  const selectedCategory = useMemo(() => {
    return categoryOptions.find(item => item._id === categoryId);
  }, [categoryId])

  const handleSave = async (data: ProductForm) => {
      try {
        if (data.weight?.quantity && data.weight?.unit) {
          if (data.weight.unit !== 'kg' && data.weight.unit !== 'l' && !Number.isInteger(Number(data.weight.quantity))) {
            setError('weight.quantity', { 
              message: `Only whole numbers are allowed for ${weightUnitOptions.find(opt => opt.value === data.weight?.unit)?.label}` 
            });
            toast.error(`Only whole numbers are allowed for ${weightUnitOptions.find(opt => opt.value === data.weight?.unit)?.label}`)
            return;
          }
        }
        
        if (!data.name || data.name.length <= 2) {
          setError('name', { message: "Name is required." })
          return
        }
        
        if (data.barcode && data.barcode.length < 3) {
          setError('barcode', { message: "Barcode must be at least 3 characters" })
          return
        }
        
        const newData = removeEmptyValues(finalProductCreationMapper(data));
        const response = await createProduct(newData).unwrap();
        
        if (response) {
          toast.success('Product Added successfully')
          reset();
          fileUploaderRef.current?.clearFiles();
          if (onSuccess) {
            onSuccess(response);
          } else {
            navigate({ to: "/product" });
          }
        }
      } catch (err: any) {
        toast.error(err?.data?.message ?? "Something went wrong")
      }
  };

  const handleSaveAndPrintBarcode = async (data: ProductForm) => {
    try {
      if (data.weight?.quantity && data.weight?.unit) {
        if (data.weight.unit !== 'kg' && data.weight.unit !== 'l' && !Number.isInteger(Number(data.weight.quantity))) {
          setError('weight.quantity', { 
            message: `Only whole numbers are allowed for ${weightUnitOptions.find(opt => opt.value === data.weight?.unit)?.label}` 
          });
          toast.error(`Only whole numbers are allowed for ${weightUnitOptions.find(opt => opt.value === data.weight?.unit)?.label}`)
          return;
        }
      }
      
      if (!data.name || data.name.length <= 2) {
        setError('name', { message: "Name is required." })
        return
      }
      if (data.barcode && data.barcode.length < 3) {
        setError('barcode', { message: "Barcode must be at least 3 characters" })
        return
      }
      const newData = removeEmptyValues(finalProductCreationMapper(data));
      const response = await createProduct(newData).unwrap();
      if (response) {
        toast.success('Product Added successfully')
        await handlePrintBarcode({
          mrp: data.mrp,
          sellingPrice: data.sellingPrice,
          productName: data?.name,
          barcode: data.barcode as string,
          storeName: currentUser?.name as string,
          productCode: data?.productCode as string,
          slug: newData.variationList?.at(0).variationId,
          quantity: data.quantity,
        })

        reset(initialProductValue);
        fileUploaderRef.current?.clearFiles();
        if (onSuccess) {
          onSuccess(response);
        } else {
          navigate({ to: "/product" });
        }
      }
    } catch (err: any) {
      toast.error(err?.data?.message ?? "Something went wrong")
    }
  };

  const fileUploaderRef = useRef<FileUploaderRef>(null);



  const handleFilesSelected = (files: string[]) => {
    const validFiles = files.filter(file => file && typeof file === 'string');
    setValue("imageList", validFiles);
  }

  const { data: productUnitOptions = [] } = useGetProductUnitListQuery({
    limit: 10,
  });

  const renderItem = (item: Product) => {
    return (
      <div
        className="dropdownItem"
        onClick={() => {
          reset(mapProductToEditableForm(item))
          setShowSuggestions(false)
        }}
      >
        <img src={item.imageList?.at(0)} alt={item.name} width={40} height={40} />
        <div className="item-info">
          <strong>{item.name}</strong>
          <span>{currencyFormatter(item?.mrp)}</span>
        </div>
      </div>
    );
  };

  const [getProductCount] = useLazyGetStoreProductCountQuery()
  async function handleClickOnGenrate() {
    const response = await getProductCount(null).unwrap()
    const count = Number(response?.data || 0);
    const storePrefix = "P";
    const serial = String(count + 1).padStart(3, "0");
    const barcodeValue = `${storePrefix}${serial}`;
    setValue('barcode', barcodeValue);
  }


  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowSuggestions(false); // <-- trigger close
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [showSuggestions]);

  return (<div className="layout">
    <main className="main">
      <div className="card">
        <h2 className="sectionHeading">Basic Information</h2>
        <div className='formGroup' style={{ position: "relative" }}>
          <label className="cardHeading">
            Product Name
            <span className="required">*</span>
          </label>
          <Controller
            control={control}
            name="name"
            render={({ field: { onBlur, onChange, value }, fieldState: { error } }) => (
              <>
                <input
                  type="text"
                  placeholder="Enter Product name"
                  className="formControl"
                  onBlur={onBlur}
                  value={value}
                  onChange={(event) => {
                    onChange(event.target.value);
                  }}
                />
                {error && <p className="error">{error.message}</p>}
              </>
            )}
          />
        </div>

        <FormTextarea
          label="Description"
          name="description"
          register={register}
          placeholder="Enter product description"
          error={errors.description}
          rows={4}
        />
        <div className="gridTwoCol">
          <FormInput
            label="HSN Code"
            name="hsnCode"
            register={register}
            placeholder="Enter HSN code"
            error={errors.hsnCode}
          />
          <FormInput
            label="Product Code"
            name="productCode"
            register={register}
            type="text"
            placeholder="Enter product code"
            error={errors.productCode}
            uppercase={true}
          />
        </div>
        <div className="barcodeInputField">
          <FormInput
            label="Barcode"
            name="barcode"
            register={register}
            placeholder="Enter barcode"
            error={errors.barcode}
          />
          <button className="genrateIcon" onClick={handleClickOnGenrate}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width={24}
              height={24}
              viewBox="0 0 24 24"
              fill="none"
              color={appColors.linkBlue}
            >
              <path
                d="M3.81836 14.4541C4.0353 14.4541 4.24308 14.5409 4.39648 14.6943C4.54988 14.8477 4.63669 15.0555 4.63672 15.2725V18.5449C4.63672 18.7619 4.72254 18.9706 4.87598 19.124C5.0293 19.2773 5.2373 19.3632 5.4541 19.3633H8.72754C8.94446 19.3633 9.15227 19.4501 9.30566 19.6035C9.45905 19.7569 9.54588 19.9647 9.5459 20.1816C9.5459 20.3986 9.4591 20.6073 9.30566 20.7607C9.15231 20.9139 8.9443 20.9999 8.72754 21H5.4541C4.8033 20.9999 4.17896 20.7414 3.71875 20.2812C3.25843 19.8209 3 19.1959 3 18.5449V15.2725C3.00003 15.0557 3.08606 14.8477 3.23926 14.6943C3.3927 14.5409 3.60136 14.4541 3.81836 14.4541ZM20.1816 14.4541C20.3986 14.4541 20.6073 14.5409 20.7607 14.6943C20.914 14.8477 21 15.0556 21 15.2725V18.5449C21 19.1959 20.7416 19.8209 20.2812 20.2812C19.821 20.7414 19.1967 20.9999 18.5459 21H15.2725C15.0557 20.9999 14.8477 20.9139 14.6943 20.7607C14.5409 20.6073 14.4551 20.3986 14.4551 20.1816C14.4551 19.9647 14.541 19.7569 14.6943 19.6035C14.8477 19.4502 15.0556 19.3634 15.2725 19.3633H18.5459C18.7627 19.3632 18.9707 19.2773 19.124 19.124C19.2775 18.9706 19.3643 18.7619 19.3643 18.5449V15.2725C19.3643 15.0556 19.4502 14.8477 19.6035 14.6943C19.7569 14.541 19.9648 14.4542 20.1816 14.4541ZM10.3633 12.8184C10.5803 12.8184 10.7889 12.9042 10.9424 13.0576C11.0957 13.2109 11.1815 13.4189 11.1816 13.6357V16.9092C11.1816 17.126 11.0957 17.3339 10.9424 17.4873C10.7889 17.6407 10.5803 17.7275 10.3633 17.7275H7.09082C6.87389 17.7275 6.6661 17.6407 6.5127 17.4873C6.35931 17.3339 6.27252 17.1261 6.27246 16.9092V13.6357C6.27258 13.4189 6.35936 13.211 6.5127 13.0576C6.66609 12.9043 6.87394 12.8184 7.09082 12.8184H10.3633ZM16.9092 12.8184C17.1262 12.8184 17.3348 12.9042 17.4883 13.0576C17.6415 13.2109 17.7274 13.419 17.7275 13.6357V16.9092C17.7275 17.1259 17.6415 17.334 17.4883 17.4873C17.3348 17.6407 17.1262 17.7275 16.9092 17.7275H16.0908C15.8739 17.7275 15.6661 17.6407 15.5127 17.4873C15.3593 17.3339 15.2725 17.1261 15.2725 16.9092C15.2725 16.6922 15.3593 16.4835 15.5127 16.3301C15.6661 16.1769 15.874 16.0909 16.0908 16.0908V13.6357C16.0909 13.4189 16.1777 13.211 16.3311 13.0576C16.4845 12.9043 16.6923 12.8184 16.9092 12.8184ZM13.6367 16.0908C13.8535 16.0909 14.0615 16.1769 14.2148 16.3301C14.3683 16.4835 14.4551 16.6922 14.4551 16.9092C14.455 17.0709 14.4063 17.2288 14.3164 17.3633C14.2265 17.4978 14.0987 17.6031 13.9492 17.665C13.7998 17.7268 13.6351 17.7425 13.4766 17.7109C13.318 17.6793 13.172 17.6016 13.0576 17.4873C12.9433 17.3729 12.8655 17.227 12.834 17.0684C12.8025 16.9097 12.819 16.7451 12.8809 16.5957C12.9428 16.4463 13.0472 16.3184 13.1816 16.2285C13.3162 16.1386 13.4749 16.0908 13.6367 16.0908ZM7.90918 14.4541V16.0908H9.5459V14.4541H7.90918ZM14.4551 12.8184C14.6719 12.8185 14.8799 12.9043 15.0332 13.0576C15.1865 13.211 15.2723 13.4189 15.2725 13.6357C15.2725 13.8527 15.1866 14.0614 15.0332 14.2148C14.8799 14.3681 14.6719 14.454 14.4551 14.4541C14.4551 14.6711 14.3683 14.8798 14.2148 15.0332C14.0615 15.1864 13.8535 15.2724 13.6367 15.2725C13.4197 15.2725 13.2111 15.1866 13.0576 15.0332C12.9042 14.8798 12.8184 14.6711 12.8184 14.4541V13.6357C12.8185 13.419 12.9044 13.2109 13.0576 13.0576C13.2111 12.9042 13.4197 12.8184 13.6367 12.8184H14.4551ZM10.3633 6.27246C10.5802 6.27246 10.789 6.35836 10.9424 6.51172C11.0958 6.66516 11.1816 6.87382 11.1816 7.09082V10.3633C11.1816 10.5803 11.0958 10.7889 10.9424 10.9424C10.789 11.0957 10.5802 11.1816 10.3633 11.1816H7.09082C6.87404 11.1816 6.66607 11.0956 6.5127 10.9424C6.35925 10.7889 6.27246 10.5803 6.27246 10.3633V7.09082C6.27246 6.87382 6.35925 6.66516 6.5127 6.51172C6.66607 6.35852 6.87403 6.2725 7.09082 6.27246H10.3633ZM17.2168 3.40918C17.3024 3.40919 17.3862 3.43428 17.457 3.48242C17.5279 3.53071 17.5829 3.59989 17.6143 3.67969L17.8271 4.19336C18.1853 5.06603 18.8657 5.76824 19.7266 6.15527L20.332 6.42578C20.4092 6.46152 20.4746 6.51829 20.5205 6.58984C20.5665 6.6617 20.5908 6.74577 20.5908 6.83105C20.5908 6.91628 20.5664 6.99947 20.5205 7.07129C20.4746 7.1431 20.4094 7.20051 20.332 7.23633L19.6904 7.52148C18.8509 7.8978 18.1829 8.57478 17.8184 9.41895L17.6104 9.89648C17.5779 9.97409 17.5232 10.0403 17.4531 10.0869C17.3831 10.1335 17.301 10.1592 17.2168 10.1592C17.1325 10.1592 17.0496 10.1336 16.9795 10.0869C16.9096 10.0402 16.8546 9.97402 16.8223 9.89648L16.6152 9.41895C16.2503 8.57495 15.5819 7.89841 14.7422 7.52246L14.1016 7.23633C14.0239 7.20062 13.9582 7.14323 13.9121 7.07129C13.866 6.99936 13.8408 6.9155 13.8408 6.83008C13.8409 6.74485 13.8661 6.66164 13.9121 6.58984C13.9582 6.51792 14.0239 6.46052 14.1016 6.4248L14.7061 6.15527C15.567 5.76861 16.2469 5.06684 16.6055 4.19434L16.8193 3.67969C16.8507 3.59989 16.9057 3.53071 16.9766 3.48242C17.0474 3.43431 17.1312 3.40918 17.2168 3.40918ZM8.72754 3C8.94439 3.00005 9.15229 3.08596 9.30566 3.23926C9.4591 3.3927 9.5459 3.60136 9.5459 3.81836C9.54577 4.03519 9.45899 4.24315 9.30566 4.39648C9.15228 4.54979 8.9444 4.63569 8.72754 4.63574H5.4541C5.23727 4.63587 5.02931 4.72265 4.87598 4.87598C4.72267 5.02936 4.63676 5.23724 4.63672 5.4541V8.72656C4.63672 8.94356 4.54992 9.15222 4.39648 9.30566C4.2431 9.45896 4.03522 9.54488 3.81836 9.54492C3.60136 9.54492 3.3927 9.4591 3.23926 9.30566C3.08591 9.15224 3 8.94349 3 8.72656V5.4541C3.00005 4.80324 3.25856 4.17902 3.71875 3.71875C4.17896 3.25854 4.80328 3.00012 5.4541 3H8.72754ZM7.90918 7.90918V9.54492H9.5459V7.90918H7.90918Z"
                fill={appColors.linkBlue}
              />
            </svg>
            <span>Generate</span>
          </button>
        </div>
      </div>
      <div className="card ">
        <h2 className="sectionHeading">Pricing</h2>
        <div className="gridTwoCol">
          <FormInput
            label="MRP"
            name="mrp"
            required={true}
            register={register}
            type="number"
            step="0.01"
            placeholder="Enter MRP"
            error={errors.mrp}
          />

          <FormInput
            label="Purchase Price"
            name="purchasePrice"
            register={register}
            type="number"
            step="0.01"
            placeholder="Enter purchase price"
            error={errors.purchasePrice}
          />

          <div className="barcodeInputField">
            <FormInput
              label="Selling Price"
              name="sellingPrice"
              register={register}
              type="number"
              step="0.01"
              placeholder="Enter selling price"
              error={errors.sellingPrice}
            />

          </div>

          <FormInput
            label="Tax Percentage"
            name="taxPercentage"
            register={register}
            type="number"
            placeholder="Enter tax percentage"
            error={errors.taxPercentage}
          />
        </div>
      </div>
      {/* Product Specifications */}
      <div className="card">
        <h2 className="sectionHeading">Product Specifications</h2>
        <div className="gridTwoCol">
          <FormInput
            label="Size"
            name="size.name"
            register={register}
            placeholder="Enter size"
            error={errors.size?.name}
          />

          <FormInput
            label="Color"
            name="color.name"
            register={register}
            placeholder="Enter color"
            error={errors.color?.name}
          />

          <FormInput
            label="Weight (Quantity)"
            name="weight.quantity"
            register={register}
            type="number"
            placeholder="Enter weight"
            error={errors.weight?.quantity}
            step={weightUnit === 'kg' || weightUnit === 'l' ? "0.01" : "1"}
            validate={(value) => {
              if (value === undefined || value === null || value === '') return true;
              const numericValue = Number(value);
              if (weightUnit && weightUnit !== 'kg' && weightUnit !== 'l') {
                return Number.isInteger(numericValue) || `Only whole numbers are allowed for ${weightUnitOptions.find(opt => opt.value === weightUnit)?.label}`;
              }
              return true;
            }}
          />
          <FormSelect
            label="Weight (Unit)"
            name="weight.unit"
            register={register}
            options={weightUnitOptions}
            error={errors.weight?.unit}
          />

        </div>
      </div>
      {/* <div className="card">
        <div className="varientSetion">
          <label className="sectionHeading">Product Variants</label>
          <button
            className="flexZero"
            type="button"
            onClick={() => {
              setCurrentVariant(null);
              setEditingVariantIndex(null);
              setVariantModalOpen(true);
            }}
          >
            + Add Variant
          </button>
        </div>
        {fields.map((field, index) => (
          <div
            key={field.id}
            className="variantItem"
            style={{
              border: `1px solid ${appColors.dividerBorder}`,
              padding: "16px",
              borderRadius: "8px",
              marginBottom: "8px",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div>
              {capitalizeFirstLetterOfWords(
                generateVariationSlug(field?.variationInfo).replaceAll(
                  "-",
                  " "
                )
              )}
            </div>
            <div style={{ display: "flex", gap: "8px" }}>
              <button
                type="button"
                onClick={() => handleEditVariant(index)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                Edit
              </button>
              <button
                type="button"
                onClick={() => handleRemoveVariant(index)}
                style={{
                  background: "none",
                  border: "none",
                  cursor: "pointer",
                  color: appColors.dangerRed,
                }}
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Variant Modal */}
      {/* {isVariantModalOpen && (
        <div className="modalBackdrop">
          <div className="modalContent">
            <div className="modalHeader">
              <h2 className="sectionHeading">
                {editingVariantIndex !== null
                  ? "Edit Variant"
                  : "Add Variant"}
              </h2>
              <button
                onClick={() => setVariantModalOpen(false)}
                style={{ fontSize: "2rem", lineHeight: "1", cursor: "pointer", border: "none", background: "none" }}
              >
                ×
              </button>
            </div>
            <div className="modalBody">
              <VariantForm
                defaultValues={currentVariant || undefined}
                onSubmit={handleAddVariant}
                variationLength={fields.length}
                onCancel={() => setVariantModalOpen(false)}
              />
            </div>
          </div>
        </div>
      )}  */}


    </main>
    <aside className="side">
      <div className="card">
        <h2 className="cardHeading">Image</h2>
        <FileUploader
          ref={fileUploaderRef}
          type={FileTypeEnum.ProductImage}
          images={imageList}
          multiple
          maxFiles={5}
          maxFileSize={5}
          allowedTypes={["image/*"]}
          onFilesSelected={handleFilesSelected}
          onError={(error) => console.error(error)}
        />
      </div>
      <div className="card">
        <h2 className="sectionHeading">Category & Brand</h2>

        <FormSelect
          label="Brand"
          name="brandId"
          register={register}
          options={brandOptions}
          error={errors.brandId}
          labelField={"name"}
          valueField="_id"
        />

        <FormSelect
          label="Category"
          name="categoryId"
          register={register}
          options={categoryOptions}
          valueField="_id"
          labelField="name"
          error={errors.categoryId}
        />
        {selectedCategory && (
          <FormSelect
            label="Sub Category"
            name="subCategoryId"
            register={register}
            options={selectedCategory.subCategoryList ?? []}
            error={errors.subCategoryId}
            valueField="_id"
            labelField="name"
          />
        )}
      </div>
      <div className="card">
        <h2 className="sectionHeading">Stock Management</h2>
        <FormSelect
          label="Product Unit"
          name="stock.productUnit"
          register={register}
          options={productUnitOptions as Array<any>}
          error={errors.stock?.productUnit}
          labelField={"viewValue.en"}
          valueField="_id"
          
        />

        <div className="gridTwoCol">
          <FormInput
            label="Stock Quantity"
            name="stock.quantity"
            register={register}
            type="number"
            placeholder="Enter quantity"
            error={errors.stock?.quantity}
          />

          <FormInput
            label="Min Quantity Alert"
            name="stock.stockMinimumQuantity"
            register={register}
            type="number"
            placeholder="Enter minimum quantity"
            error={errors.stock?.stockMinimumQuantity}
          />
        </div>
      </div>



      <div className="card rowContainer">
        <Button type='reset' variant="outline" size={isBarcodeAvailable ? "lg" : "xl"}
          // disabled={!isUpdated || isLoading || isSubmitting || !isDirty}
          onClick={() => reset()}>
          RESET
        </Button>
        <Button type='reset' size={isBarcodeAvailable ? "lg" : "xl"}
           disabled={isLoading}
          onClick={handleSubmit(handleSave)}>
          SAVE
        </Button>
        {!!isBarcodeAvailable && <Button type='reset' size='lg'
          onClick={handleSubmit(handleSaveAndPrintBarcode)}>
          Save & Print Barcode
        </Button>}
      </div>

    </aside>
  </div>)
}

// Reusable Form Components


interface FormSelectProps<T extends FieldValues, O extends object = Record<string, unknown>> {
  label: string;
  name: Path<T>;
  register: UseFormRegister<T>;
  options: Array<O>;
  error?: FieldError;
  className?: string;
  labelClassName?: string;
  selectClassName?: string;
  errorClassName?: string;
  required?: boolean;
  labelField?: string;
  valueField?: string;
  onChange?: (e: React.ChangeEvent<HTMLSelectElement>) => void;
}

function FormSelect<T extends FieldValues, O extends object = Record<string, unknown>>({
  label,
  name,
  register,
  options,
  error,
  className = "formGroup",
  labelClassName = "cardHeading",
  errorClassName = "error",
  required = false,
  labelField = "label",
  valueField = "value",
  onChange,
}: FormSelectProps<T, O>) {
  return (
    <div className={className}>
      <label className={labelClassName}>
        {label}
        {required && <span className="required">*</span>}
      </label>
      <select
        className="formControl"
        {...register(name)}
        onChange={onChange || register(name).onChange}
      >
        <option value="">Select {label.toLowerCase()}</option>
        {options.map((option) => (
          <option key={get(option, valueField)} value={get(option, valueField)}>
            {get(option, labelField)}
          </option>
        ))}
      </select>
      {error && <p className={errorClassName}>{error.message}</p>}
    </div>
  );
}

interface FormTextareaProps<T extends FieldValues> {
  label: string;
  name: Path<T>;
  register: UseFormRegister<T>;
  placeholder?: string;
  error?: FieldError;
  className?: string;
  labelClassName?: string;
  textareaClassName?: string;
  errorClassName?: string;
  rows?: number;
  required?: boolean;
}

function FormTextarea<T extends FieldValues>({
  label,
  name,
  register,
  placeholder = "",
  error,
  className = "formGroup",
  labelClassName = "cardHeading",
  errorClassName = "error",
  rows = 3,
  required = false,
}: FormTextareaProps<T>) {
  return (
    <div className={className}>
      <label className={labelClassName}>
        {label}
        {required && <span className="required">*</span>}
      </label>
      <textarea
        placeholder={placeholder}
        className="formControl"
        rows={rows}
        {...register(name)}
      />
      {error && <p className={errorClassName}>{error.message}</p>}
    </div>
  );
}


// interface VariantFormProps {
//   defaultValues?: Variation;
//   variationLength?: number
//   onSubmit: (variant: Variation) => void;
//   onCancel: () => void;
// }
// function VariantForm({ defaultValues, onSubmit, onCancel, variationLength = 0 }: VariantFormProps) {
//   const {
//     register,
//     handleSubmit,
//     formState: { errors },
//     watch,
//     reset,
//     setValue,
//   } = useForm<Variation>({
//     defaultValues: defaultValues || {
//       variationInfo: {
//         color: { name: "" },
//         flavor: { name: "" },
//         size: { name: "" },
//         weight: { unit: "", quantity: undefined },
//         imageList: [],
//       },
//       prodcutCode:'',
//       price: {
//         mrp: undefined,
//         sellingPrice: undefined,
//         purchasePrice: undefined,
//       },
//       stock: {
//         productUnit: undefined,
//         quantity: undefined,
//         stockMinimumQuantity: undefined,
//       },
//       minimumQuantity: {
//         quantity: 1,
//       },
//     },
//     resolver: zodResolver(VariationSchema),
//   });

//   const [getProductCount] = useLazyGetStoreProductCountQuery()
//   async function handleClickOnGenrate() {
//     const response = await getProductCount(null).unwrap()
//     const count = Number(response?.data || 0);
//     const storePrefix = "P001";
//     const serial = String(count + 2 + variationLength).padStart(6, "0");
//     const barcodeValue = `${storePrefix}${serial}`;
//     setValue('barcode', barcodeValue);
//   }

//   const { data: productUnitOptions = [] } = useGetProductUnitListQuery({
//     limit: 50,
//   });

//   const fileUploaderRef = useRef<FileUploaderRef>(null);

//   const handleFilesSelected = (files: string[]) => {
//     setValue("variationInfo.imageList", files);
//   };

//   return (
//     <form onSubmit={handleSubmit(onSubmit)} className="layout">

//       <main className="main">
//         <div className="formGroup barcodeInputField">
//           <FormInput
//             label="Barcode"
//             name="barcode"
//             register={register}
//             placeholder="Enter or generate barcode"
//             error={errors.barcode}
//           />
//           <button type="button" className="genrateIcon" onClick={handleClickOnGenrate}>
//             <svg
//               xmlns="http://www.w3.org/2000/svg"
//               width={24}
//               height={24}
//               viewBox="0 0 24 24"
//               fill="none"
//               color={appColors.contentInk
//             >
//               <path
//                 d="M3.81836 14.4541C4.0353 14.4541 4.24308 14.5409 4.39648 14.6943C4.54988 14.8477 4.63669 15.0555 4.63672 15.2725V18.5449C4.63672 18.7619 4.72254 18.9706 4.87598 19.124C5.0293 19.2773 5.2373 19.3632 5.4541 19.3633H8.72754C8.94446 19.3633 9.15227 19.4501 9.30566 19.6035C9.45905 19.7569 9.54588 19.9647 9.5459 20.1816C9.5459 20.3986 9.4591 20.6073 9.30566 20.7607C9.15231 20.9139 8.9443 20.9999 8.72754 21H5.4541C4.8033 20.9999 4.17896 20.7414 3.71875 20.2812C3.25843 19.8209 3 19.1959 3 18.5449V15.2725C3.00003 15.0557 3.08606 14.8477 3.23926 14.6943C3.3927 14.5409 3.60136 14.4541 3.81836 14.4541ZM20.1816 14.4541C20.3986 14.4541 20.6073 14.5409 20.7607 14.6943C20.914 14.8477 21 15.0556 21 15.2725V18.5449C21 19.1959 20.7416 19.8209 20.2812 20.2812C19.821 20.7414 19.1967 20.9999 18.5459 21H15.2725C15.0557 20.9999 14.8477 20.9139 14.6943 20.7607C14.5409 20.6073 14.4551 20.3986 14.4551 20.1816C14.4551 19.9647 14.541 19.7569 14.6943 19.6035C14.8477 19.4502 15.0556 19.3634 15.2725 19.3633H18.5459C18.7627 19.3632 18.9707 19.2773 19.124 19.124C19.2775 18.9706 19.3643 18.7619 19.3643 18.5449V15.2725C19.3643 15.0556 19.4502 14.8477 19.6035 14.6943C19.7569 14.541 19.9648 14.4542 20.1816 14.4541ZM10.3633 12.8184C10.5803 12.8184 10.7889 12.9042 10.9424 13.0576C11.0957 13.2109 11.1815 13.4189 11.1816 13.6357V16.9092C11.1816 17.126 11.0957 17.3339 10.9424 17.4873C10.7889 17.6407 10.5803 17.7275 10.3633 17.7275H7.09082C6.87389 17.7275 6.6661 17.6407 6.5127 17.4873C6.35931 17.3339 6.27252 17.1261 6.27246 16.9092V13.6357C6.27258 13.4189 6.35936 13.211 6.5127 13.0576C6.66609 12.9043 6.87394 12.8184 7.09082 12.8184H10.3633ZM16.9092 12.8184C17.1262 12.8184 17.3348 12.9042 17.4883 13.0576C17.6415 13.2109 17.7274 13.419 17.7275 13.6357V16.9092C17.7275 17.1259 17.6415 17.334 17.4883 17.4873C17.3348 17.6407 17.1262 17.7275 16.9092 17.7275H16.0908C15.8739 17.7275 15.6661 17.6407 15.5127 17.4873C15.3593 17.3339 15.2725 17.1261 15.2725 16.9092C15.2725 16.6922 15.3593 16.4835 15.5127 16.3301C15.6661 16.1769 15.874 16.0909 16.0908 16.0908V13.6357C16.0909 13.4189 16.1777 13.211 16.3311 13.0576C16.4845 12.9043 16.6923 12.8184 16.9092 12.8184ZM13.6367 16.0908C13.8535 16.0909 14.0615 16.1769 14.2148 16.3301C14.3683 16.4835 14.4551 16.6922 14.4551 16.9092C14.455 17.0709 14.4063 17.2288 14.3164 17.3633C14.2265 17.4978 14.0987 17.6031 13.9492 17.665C13.7998 17.7268 13.6351 17.7425 13.4766 17.7109C13.318 17.6793 13.172 17.6016 13.0576 17.4873C12.9433 17.3729 12.8655 17.227 12.834 17.0684C12.8025 16.9097 12.819 16.7451 12.8809 16.5957C12.9428 16.4463 13.0472 16.3184 13.1816 16.2285C13.3162 16.1386 13.4749 16.0908 13.6367 16.0908ZM7.90918 14.4541V16.0908H9.5459V14.4541H7.90918ZM14.4551 12.8184C14.6719 12.8185 14.8799 12.9043 15.0332 13.0576C15.1865 13.211 15.2723 13.4189 15.2725 13.6357C15.2725 13.8527 15.1866 14.0614 15.0332 14.2148C14.8799 14.3681 14.6719 14.454 14.4551 14.4541C14.4551 14.6711 14.3683 14.8798 14.2148 15.0332C14.0615 15.1864 13.8535 15.2724 13.6367 15.2725C13.4197 15.2725 13.2111 15.1866 13.0576 15.0332C12.9042 14.8798 12.8184 14.6711 12.8184 14.4541V13.6357C12.8185 13.419 12.9044 13.2109 13.0576 13.0576C13.2111 12.9042 13.4197 12.8184 13.6367 12.8184H14.4551ZM10.3633 6.27246C10.5802 6.27246 10.789 6.35836 10.9424 6.51172C11.0958 6.66516 11.1816 6.87382 11.1816 7.09082V10.3633C11.1816 10.5803 11.0958 10.7889 10.9424 10.9424C10.789 11.0957 10.5802 11.1816 10.3633 11.1816H7.09082C6.87404 11.1816 6.66607 11.0956 6.5127 10.9424C6.35925 10.7889 6.27246 10.5803 6.27246 10.3633V7.09082C6.27246 6.87382 6.35925 6.66516 6.5127 6.51172C6.66607 6.35852 6.87403 6.2725 7.09082 6.27246H10.3633ZM17.2168 3.40918C17.3024 3.40919 17.3862 3.43428 17.457 3.48242C17.5279 3.53071 17.5829 3.59989 17.6143 3.67969L17.8271 4.19336C18.1853 5.06603 18.8657 5.76824 19.7266 6.15527L20.332 6.42578C20.4092 6.46152 20.4746 6.51829 20.5205 6.58984C20.5665 6.6617 20.5908 6.74577 20.5908 6.83105C20.5908 6.91628 20.5664 6.99947 20.5205 7.07129C20.4746 7.1431 20.4094 7.20051 20.332 7.23633L19.6904 7.52148C18.8509 7.8978 18.1829 8.57478 17.8184 9.41895L17.6104 9.89648C17.5779 9.97409 17.5232 10.0403 17.4531 10.0869C17.3831 10.1335 17.301 10.1592 17.2168 10.1592C17.1325 10.1592 17.0496 10.1336 16.9795 10.0869C16.9096 10.0402 16.8546 9.97402 16.8223 9.89648L16.6152 9.41895C16.2503 8.57495 15.5819 7.89841 14.7422 7.52246L14.1016 7.23633C14.0239 7.20062 13.9582 7.14323 13.9121 7.07129C13.866 6.99936 13.8408 6.9155 13.8408 6.83008C13.8409 6.74485 13.8661 6.66164 13.9121 6.58984C13.9582 6.51792 14.0239 6.46052 14.1016 6.4248L14.7061 6.15527C15.567 5.76861 16.2469 5.06684 16.6055 4.19434L16.8193 3.67969C16.8507 3.59989 16.9057 3.53071 16.9766 3.48242C17.0474 3.43431 17.1312 3.40918 17.2168 3.40918ZM8.72754 3C8.94439 3.00005 9.15229 3.08596 9.30566 3.23926C9.4591 3.3927 9.5459 3.60136 9.5459 3.81836C9.54577 4.03519 9.45899 4.24315 9.30566 4.39648C9.15228 4.54979 8.9444 4.63569 8.72754 4.63574H5.4541C5.23727 4.63587 5.02931 4.72265 4.87598 4.87598C4.72267 5.02936 4.63676 5.23724 4.63672 5.4541V8.72656C4.63672 8.94356 4.54992 9.15222 4.39648 9.30566C4.2431 9.45896 4.03522 9.54488 3.81836 9.54492C3.60136 9.54492 3.3927 9.4591 3.23926 9.30566C3.08591 9.15224 3 8.94349 3 8.72656V5.4541C3.00005 4.80324 3.25856 4.17902 3.71875 3.71875C4.17896 3.25854 4.80328 3.00012 5.4541 3H8.72754ZM7.90918 7.90918V9.54492H9.5459V7.90918H7.90918Z"
//                 fill={appColors.contentInk
//               />
//             </svg>
//             <span>Generate</span>
//           </button>

//         </div>
//              <FormInput
//             label="Product Code"
//             name="productCode"
//             register={register}
//             type="text"
//             placeholder="Enter product code"
//             error={errors.productCode}
//           />

//         {/* Pricing */}
//         <h2 className="sectionHeading">Pricing</h2>
//         <div className="gridTwoCol">
//           <FormInput
//             label="MRP"
//             name="price.mrp"
//             register={register}
//             type="number"
//             placeholder="Enter MRP"
//             error={errors.price?.mrp}
//           />
//           <FormInput
//             label="Purchase Price"
//             name="price.purchasePrice"
//             register={register}
//             type="number"
//             placeholder="Enter purchase price"
//             error={errors.price?.purchasePrice}
//           />
//           <FormInput
//             label="Selling Price"
//             name="price.sellingPrice"
//             register={register}
//             type="number"
//             placeholder="Enter selling price"
//             error={errors.price?.sellingPrice}
//           />
//         </div>

//         {/* Product Specifications */}
//         <h2 className="sectionHeading">Product Specifications</h2>
//         <div className="gridTwoCol">
//           <FormInput
//             label="Size"
//             name="variationInfo.size.name"
//             register={register}
//             placeholder="Enter size"
//             error={errors.variationInfo?.size?.name}
//           />
//           <FormInput
//             label="Color"
//             name="variationInfo.color.name"
//             register={register}
//             placeholder="Enter color"
//             error={errors.variationInfo?.color?.name}
//           />
//           <FormInput
//             label="Weight (Quantity)"
//             name="variationInfo.weight.quantity"
//             register={register}
//             type="number"
//             placeholder="Enter weight"
//             error={errors.variationInfo?.weight?.quantity}
//           />
//           <FormSelect
//             label="Weight (Unit)"
//             name="variationInfo.weight.unit"
//             register={register}
//             options={weightUnitOptions}
//             error={errors.variationInfo?.weight?.unit}
//           />
//           <FormInput
//             label="Flavor"
//             name="variationInfo.flavor.name"
//             register={register}
//             placeholder="Enter flavor"
//             error={errors.variationInfo?.flavor?.name}
//           />
//           <FormInput
//             label="Min Sell Quantity"
//             name="minimumQuantity.quantity"
//             register={register}
//             type="number"
//             placeholder="Enter minimum sell quantity"
//             error={errors.minimumQuantity?.quantity}
//           />
//         </div>
//       </main>
//       <aside className="side">
//         <div className="formGroup">
//           <label className="cardHeading">Image</label>
//           <FileUploader
//             ref={fileUploaderRef}
//             multiple
//             maxFiles={5}
//             maxFileSize={5}
//             type={FileTypeEnum.ProductImage}
//             allowedTypes={["image/*"]}
//             onFilesSelected={handleFilesSelected}
//             onError={(error) => console.error(error)}
//           />
//         </div>

//         {/* Stock Management */}
//         <h2 className="sectionHeading">Stock Management</h2>
//         <FormSelect
//           label="Unit"
//           name="stock.productUnit"
//           register={register}
//           options={productUnitOptions}
//           error={errors.stock?.productUnit}
//           labelField={"viewValue.en"}
//         />
//         <div className="gridTwoCol">
//           <FormInput
//             label="Stock Quantity"
//             name="stock.quantity"
//             register={register}
//             type="number"
//             placeholder="Enter quantity"
//             error={errors.stock?.quantity}
//           />
//           <FormInput
//             label="Min Quantity Alert"
//             name="stock.stockMinimumQuantity"
//             register={register}
//             type="number"
//             placeholder="Enter min quantity"
//             error={errors.stock?.stockMinimumQuantity}
//           />
//         </div>

//         <div className="rowContainer buttons">
//           <Button type='reset' variant="outline" size="xl"
//             // disabled={!isUpdated || isLoading || isSubmitting || !isDirty}
//             onClick={() => reset()}>
//             RESET
//           </Button>
//           <Button type='reset' size='xl'
//             //  disabled={isLoading || isSubmitting || !isDirty}
//             onClick={handleSubmit(onSubmit)}>
//             SAVE
//           </Button>
//         </div>
//       </aside>
//     </form>
//   );
// }